import type { Theme } from '@mui/material';
import { Box, Divider, Typography } from '@mui/material';
import type { ComponentProps } from 'react';
import { cardVariants } from '@/pages/ValuationsV2/theme';
import { combineSx, type SXVariantProps, sxVariants } from '@/utils/combineSx';
import { pxToRem } from '@/utils/pxToRem';
import { stringToAttr } from '@/utils/stringToAttr';

const colStyles = {
  col: {
    position: 'relative',
    display: 'grid',
    height: '100%',
    width: '100%',
    gap: '1rem',
    gridTemplateRows: 'auto 1fr',
    gridAutoFlow: 'row',
    padding: '1rem',
  },
  colTitle: (theme: Theme) => ({
    fontSize: theme.fontSizes.smaller2,
    fontWeight: 700,
    letterSpacing: '0.08em',
    color: theme.palette.text.secondary,
    lineHeight: '1.25',
  }),
  subCol: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    gap: '1rem',
    marginTop: '0.5rem',
  },
  subColTitle: {
    textTransform: 'uppercase',
  },

  actions: {
    position: 'relative',
    marginTop: 'auto',
    display: 'grid',
    width: 'calc(100% + 2rem)',
    placeItems: 'center',
    gridTemplateColumns: '1fr',
    gridAutoFlow: 'column',
    gridAutoColumns: 'min-content',
    color: 'inherit',
    marginLeft: '-1rem',
    '--borderColor': 'color-mix(in srgb, currentColor 15%, transparent)',
    borderTop: `1px solid var(--borderColor)`,
    '& :is(button, a)': {
      color: 'inherit',
      borderRadius: 0,
      border: 0,
      '&:not(:last-child)': {
        borderRight: `1px solid var(--borderColor)`,
      },
    },
  },
};

const colVariants = sxVariants({
  base: combineSx(cardVariants({ interactive: false, padding: 'compact' }), colStyles.col),
  variants: {
    variant: {
      default: {
        '&:has(.actions)': {
          paddingBottom: 0,
        },
      },
      highlighted: {
        position: 'relative',
        padding: '0.675rem 0.75rem',
        color: theme => `${theme.palette.baseColors.deepSeaNavy.c100}`,
        background: theme => `${theme.palette.baseColors.skyBlue.c25}`,
        border: theme => `${pxToRem(4)} solid ${theme.palette.baseColors.white.c100}`,
        backgroundOrigin: 'padding-box',
        backgroundClip: 'padding-box',
        '&:has(.actions)': {
          paddingBottom: 0,
        },
      },
    },
  },
});

const colTitleVariants = sxVariants({
  base: colStyles.colTitle,
  variants: {
    variant: {
      default: {},
      highlighted: {
        color: 'inherit',
      },
    },
  },
});

type ColProps<Area = string> = SXVariantProps<typeof colVariants> &
  ComponentProps<typeof Box> & {
    columnTitle?: string;
    actions?: React.ReactNode;
    gridArea: Area;
  };

const Col = <Area,>({
  columnTitle,
  children,
  variant = 'default',
  sx,
  ...props
}: ColProps<Area>) => {
  const dataId = columnTitle ? `col-${stringToAttr(columnTitle)}` : 'col';

  return (
    <Box
      component='article'
      className='col'
      sx={combineSx(colVariants({ variant }), sx)}
      data-id={dataId}
      {...props}
    >
      {columnTitle ? (
        <Typography variant='overline' sx={colTitleVariants({ variant })}>
          {columnTitle}
        </Typography>
      ) : null}

      {children}
    </Box>
  );
};

type SubColProps = ComponentProps<typeof Box> & {
  subColumnTitle: string;
  textTransform?: 'none' | 'uppercase';
};

const SubCol = ({
  subColumnTitle,
  textTransform = 'uppercase',
  children,
  ...props
}: SubColProps & { children: React.ReactNode }) => {
  return (
    <Box
      sx={colStyles.subCol}
      data-id={`summary-subcolumn-${stringToAttr(subColumnTitle)}`}
      {...props}
    >
      <Typography variant='overline' sx={{ textTransform }}>
        {subColumnTitle}
      </Typography>
      <Divider />
      {children}
    </Box>
  );
};

const colActionsVariants = sxVariants({
  base: colStyles.actions,
  variants: {
    variant: {
      highlighted: {
        width: 'calc(100% + 1rem)',
      },
      default: {},
    },
  },
});

type ColActionsProps = SXVariantProps<typeof colActionsVariants> &
  ComponentProps<typeof Box> & {
    actions?: React.ReactNode;
  };

const ColActions = ({ children, sx, variant = 'default' }: ColActionsProps) => {
  return (
    <Box className='actions' sx={combineSx(colActionsVariants({ variant }), sx)}>
      {children}
    </Box>
  );
};

const colGroupStyles = {
  group: {
    display: 'grid',
    gridTemplateRows: '1fr',
    gridAutoFlow: 'row',
    gridAutoRows: 'max-content',
    gap: 'inherit',
    '--border': pxToRem(4),
    borderRadius: 'var(--border)',
    border: 'var(--border) solid light-dark(oklch(0 0 0 / 0.05), oklch(0.9 0 0 / 0.1))',
    background: 'light-dark(oklch(0 0 0 / 0.05), oklch(0.5 0 0 / 0.1)) padding-box',

    '& > .col': {
      border: 0,
      borderRadius: 'calc(var(--border) - (var(--border)/2))',
    },
  },
};

type ColGroupProps<Area = string> = ComponentProps<typeof Box> & {
  gridArea?: Area;
};

const ColGroup = <Area,>({ sx, ...props }: ColGroupProps<Area>) => {
  return (
    <Box
      className='group'
      data-id='col-group'
      sx={combineSx(colGroupStyles.group, sx)}
      {...props}
    />
  );
};

export { Col, ColGroup, SubCol, ColActions };
